// Global "AI Playground" launcher — a command palette opened with ⌘K / Ctrl+K from any
// authenticated page. It captures a goal and hands it to the AI Playground page via
// nav state (`launchGoal`), where the real run composer takes over: extension
// scoping and the actual send stay on that page, so there is ONE run code path.
// (Mirrors AppShell's "New Workflow" → Workflows `openAiGenerate` hand-off.) The
// palette itself never starts a run.

import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { TextArea } from '@progress/kendo-react-inputs';

export function AskAiLauncher() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [goal, setGoal] = useState('');
  // Lets us focus the inner <textarea> once the dialog mounts (KendoReact owns
  // the element, same approach as the Copilot composer).
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // ⌘K / Ctrl+K toggles the palette anywhere in the authenticated shell. We
  // preventDefault so the browser's own ⌘K (address-bar search in some browsers)
  // doesn't fire instead.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Focus the goal field when the palette opens.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => wrapRef.current?.querySelector('textarea')?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const close = useCallback(() => {
    setOpen(false);
    setGoal('');
  }, []);

  // Hand the goal to the AI Playground page; that page owns extension scoping + the
  // actual send. An empty goal just opens the page (no nav state).
  const launch = useCallback(() => {
    const trimmed = goal.trim();
    navigate('/ai-playground', trimmed ? { state: { launchGoal: trimmed } } : undefined);
    close();
  }, [goal, navigate, close]);

  const onComposerKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // ⌘/Ctrl+Enter opens the playground (plain Enter keeps adding lines — it's a
      // multi-line goal field, mirroring the Copilot composer shortcut).
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        launch();
      }
    },
    [launch],
  );

  if (!open) return null;

  return (
    <Dialog title="AI Playground" onClose={close} width={520}>
      <div
        ref={wrapRef}
        onKeyDown={onComposerKeyDown}
        style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 2px' }}
      >
        <TextArea
          value={goal}
          onChange={(e) => setGoal(String(e.value ?? ''))}
          rows={3}
          placeholder="Describe a goal for the agent… (you'll pick extensions on the next screen)"
          aria-label="AI Playground goal"
          aria-keyshortcuts="Meta+Enter Control+Enter"
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span aria-hidden style={{ marginRight: 'auto', fontSize: 12, opacity: 0.7 }}>
            ⌘↵ to open the playground
          </span>
          <Button onClick={close}>Cancel</Button>
          <Button themeColor="primary" onClick={launch}>
            Open in AI Playground
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export default AskAiLauncher;
